import React from 'react';

const GeneralSection = ({ formData, isEditing, onInputChange }) => {
  const handleInputChange = (field, value) => {
    onInputChange(field, value);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">General Information</h2>
        <p className="text-sm text-gray-600 mt-1">
          Configure the basic settings for your feature extraction job
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Slug Field */}
        <div>
          <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-2">
            Slug <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="slug"
            value={formData.slug || ''}
            onChange={(e) => handleInputChange('slug', e.target.value)}
            disabled={!isEditing}
            placeholder="Enter data slug (e.g., my-dataset)"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            This will be used to locate your data at data/{formData.slug || '[slug]'}/
          </p>
        </div>

        {/* Name Field */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Job Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            value={formData.name || ''}
            onChange={(e) => handleInputChange('name', e.target.value)}
            disabled={!isEditing}
            placeholder="Enter a descriptive name for this job"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
            required
          />
        </div>

        {/* Quality Threshold Field */}
        <div>
          <label htmlFor="quality_threshold" className="block text-sm font-medium text-gray-700 mb-2">
            Quality Threshold <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            id="quality_threshold"
            min="0"
            max="100"
            step="any"
            value={formData.quality_threshold ?? ''}
            onChange={(e) => handleInputChange('quality_threshold', e.target.value === '' ? '' : Number(e.target.value))}
            disabled={!isEditing}
            placeholder="50"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Minimum MSRep library match Quality (0-100) a metabolite must reach to be kept. Defaults to 50.
          </p>
        </div>
      </div>

      {/* Description Field - Full width */}
      <div className="mt-6">
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          id="description"
          value={formData.description || ''}
          onChange={(e) => handleInputChange('description', e.target.value)}
          disabled={!isEditing}
          placeholder="Describe the purpose and details of this feature extraction job"
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 resize-vertical"
        />
      </div>

      {/* Data Source Info */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
        <div className="flex items-start space-x-2">
          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-blue-900">Data Source Configuration</h4>
            <p className="text-sm text-blue-700 mt-1">
              The system will automatically look for data at <code className="bg-blue-100 px-1 rounded">data/{formData.slug || '[slug]'}/</code> and use <code className="bg-blue-100 px-1 rounded">demographics.csv</code> as the demographics file.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralSection;
